import React from 'react';
import { useTranslation } from 'react-i18next';
import './CentralUsageCard.scss';

type CentralStatus = 'normal' | 'warning' | 'critical';

interface CentralUsageCardProps {
  volume: number;
  limit: number;
  warningThreshold?: number;
  criticalThreshold?: number;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

const getStatus = (
  ratio: number,
  warningThreshold: number,
  criticalThreshold: number,
): CentralStatus => {
  if (ratio >= criticalThreshold) {
    return 'critical';
  }

  if (ratio >= warningThreshold) {
    return 'warning';
  }

  return 'normal';
};

const formatValue = (value: number): string =>
  value.toLocaleString('pt-BR');

export const CentralUsageCard: React.FC<CentralUsageCardProps> = ({
  volume,
  limit,
  warningThreshold = 0.8,
  criticalThreshold = 0.95,
}) => {
  const { t } = useTranslation();
  const safeLimit = limit > 0 ? limit : 1;
  const ratio = volume / safeLimit;
  const progressPercent = clamp(ratio * 100, 0, 100);
  const status = getStatus(ratio, warningThreshold, criticalThreshold);

  let message = '';

  if (status === 'warning') {
    message = t('central.warningMessage');
  }

  if (status === 'critical') {
    message = t('central.criticalMessage');
  }

  return (
    <section
      className={`central-usage-card central-usage-card--${status}`}
      aria-label={t('central.accountData')}
    >
      <span className="central-usage-card__title">{t('central.accountData')}</span>

      <div className="central-usage-card__content">
        <div className="central-usage-card__numbers">
          <span>{formatValue(volume)}</span>
          <span>{formatValue(limit)}</span>
        </div>

        <div className="central-usage-card__track" aria-hidden="true">
          <div className="central-usage-card__line" />
          <div
            className="central-usage-card__progress"
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        <div className="central-usage-card__labels">
          <span>{t('central.interactionsVolume')}</span>
          <span>{t('central.monthlyAccountLimit')}</span>
        </div>

        {message ? (
          <p className="central-usage-card__message">
            <span className="central-usage-card__message-icon" aria-hidden="true">i</span>
            <span>{message}</span>
          </p>
        ) : null}
      </div>
    </section>
  );
};
